import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { Download, Power, Radio } from 'lucide-react';
import DocsPage from './DocsPage';
import './index.css';

const TOKEN_KEY = '@ridelayer/token';

async function download(path: string, filename: string) {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(path, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function api(path: string, options: RequestInit = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(path, { ...options, headers }).then(async (res) => {
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  });
}

type Device = {
  id: string;
  given_id: string;
  type: string;
  created_at: string;
};

function Login({ onLogin }: { onLogin: () => void }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      setStep('code');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { token } = await api('/api/auth/verify', {
        method: 'POST',
        body: JSON.stringify({ email, code }),
      });
      localStorage.setItem(TOKEN_KEY, token);
      onLogin();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <h1>Sign In</h1>
      {step === 'email' ? (
        <form onSubmit={sendCode}>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
          <button type="submit" disabled={loading}>
            {loading ? 'Sending...' : 'Send Code'}
          </button>
        </form>
      ) : (
        <form onSubmit={verify}>
          <p className="hint">Code sent to {email}</p>
          <label htmlFor="code">Verification Code</label>
          <input
            id="code"
            type="text"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            required
            autoFocus
          />
          <button type="submit" disabled={loading}>
            {loading ? 'Verifying...' : 'Verify'}
          </button>
          <button type="button" className="link-btn" onClick={() => setStep('email')}>
            Use a different email
          </button>
        </form>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function Devices({ onLogout }: { onLogout: () => void }) {
  const [devices, setDevices] = useState<Device[]>([]);
  const [supplierName, setSupplierName] = useState('');
  const [supplierSlug, setSupplierSlug] = useState('');
  const [givenId, setGivenId] = useState('');
  const [type, setType] = useState('ebike');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const [data, me] = await Promise.all([api('/api/devices'), api('/api/auth/me')]);
      setSupplierName(me.name);
      setSupplierSlug(me.slug ?? '');
      setDevices(data);
    } catch (err: any) {
      if (err.message === 'Unauthorized') {
        localStorage.removeItem(TOKEN_KEY);
        onLogout();
      }
      setError(err.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const [pingingId, setPingingId] = useState<string | null>(null);
  const [pingResult, setPingResult] = useState<string | null>(null);

  const pingDevice = async (d: Device) => {
    setError('');
    setPingResult(null);
    setPingingId(d.id);
    try {
      const { topic, nonce } = await api(`/api/devices/${d.id}/ping`, { method: 'POST' });
      setPingResult(`Sent ping to ${topic} (nonce ${nonce.slice(0, 8)})`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setPingingId(null);
    }
  };

  const register = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api('/api/devices', {
        method: 'POST',
        body: JSON.stringify({ given_id: givenId, type }),
      });
      setGivenId('');
      await load();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    onLogout();
  };

  return (
    <div className="card wide">
      <div className="header-row">
        <div>
          <h1>Devices</h1>
          {supplierName && <p className="hint">{supplierName} {supplierSlug && <code>{supplierSlug}</code>}</p>}
        </div>
        <button className="link-btn" onClick={logout}>
          <Power size={14} /> Sign Out
        </button>
      </div>

      {supplierSlug && (
        <div className="info-box">
          <p>
            Your supplier slug is <code>{supplierSlug}</code>
          </p>
          <p>Devices use MQTT topics in this format:</p>
          <pre>
{`<deviceType>/${supplierSlug}/<deviceId>/telemetry  ← device publishes
<deviceType>/${supplierSlug}/<deviceId>/control    ← device subscribes`}
          </pre>
          <p className="hint" style={{ marginBottom: 0 }}>
            Connect via MQTTS at <code>testing.mqtt.ridelayer.com:8883</code>.
            Device types: <code>vehicle</code>, <code>dock</code>, <code>charger</code>, <code>retrofit</code>. Device ID is usually the MAC address or IMEI. MAC addresses are lowercased and colons are removed (e.g. <code>AA:BB:CC</code> becomes <code>aabbcc</code>).
          </p>
          <button className="download-btn" onClick={() => download('/api/devices/ca.crt', 'ca.crt')}>
            <Download size={14} />
            <span>Download ca.crt</span>
          </button>
        </div>
      )}

      <form onSubmit={register} className="register-form">
        <input
          type="text"
          value={givenId}
          onChange={(e) => setGivenId(e.target.value)}
          placeholder="Device ID (usually MAC or IMEI)"
          required
        />
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="ebike">eBike</option>
          <option value="charger">Charger</option>
          <option value="dock">Dock</option>
          <option value="retrofit">Retrofit</option>
        </select>
        <button type="submit" disabled={loading}>
          {loading ? 'Registering...' : 'Register'}
        </button>
      </form>

      {error && <p className="error">{error}</p>}

      {devices.length === 0 ? (
        <p className="hint">No devices registered yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Device ID</th>
              <th>Type</th>
              <th>Certs</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => (
              <tr key={d.id}>
                <td>{d.given_id}</td>
                <td>{d.type}</td>
                <td className="cert-links">
                  <button className="link-btn" onClick={() => download(`/api/devices/${d.id}/device.crt`, `${d.given_id}.crt`)}>device.crt</button>
                  <button className="link-btn" onClick={() => download(`/api/devices/${d.id}/device.key`, `${d.given_id}.key`)}>device.key</button>
                </td>
                <td>{new Date(d.created_at).toLocaleDateString()}</td>
                <td>
                  <button
                    className="ping-btn"
                    disabled={pingingId === d.id}
                    onClick={() => pingDevice(d)}
                  >
                    <Radio size={14} /> {pingingId === d.id ? 'Pinging...' : 'Ping'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pingResult && <p className="hint">{pingResult}</p>}
    </div>
  );
}

function TopNav({ authed }: { authed: boolean }) {
  return (
    <header className="top-nav">
      <div className="top-nav-brand">RideLayer</div>
      <nav className="top-nav-links">
        <NavLink to="/" end className={({ isActive }) => `top-nav-link${isActive ? ' active' : ''}`}>
          Devices
        </NavLink>
        {authed && (
          <NavLink to="/docs" className={({ isActive }) => `top-nav-link${isActive ? ' active' : ''}`}>
            API Reference
          </NavLink>
        )}
      </nav>
    </header>
  );
}

function DevicesPage({ authed, onLogin, onLogout }: { authed: boolean; onLogin: () => void; onLogout: () => void }) {
  return (
    <div className="page-centered">
      {authed
        ? <Devices onLogout={onLogout} />
        : <Login onLogin={onLogin} />
      }
    </div>
  );
}

export function App() {
  const [authed, setAuthed] = useState(() => !!localStorage.getItem(TOKEN_KEY));

  return (
    <BrowserRouter>
      <TopNav authed={authed} />
      <Routes>
        <Route path="/" element={
          <DevicesPage
            authed={authed}
            onLogin={() => setAuthed(true)}
            onLogout={() => setAuthed(false)}
          />
        } />
        <Route path="/docs" element={authed ? <DocsPage /> : <Navigate to="/" replace />} />
        <Route path="/docs/:device" element={authed ? <DocsPage /> : <Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
